/**
 * Builds the URL for app.diagrams.net with OneDrive hash (#W{id}).
 *
 * Note: With the Graph API drive item ID (Teams/SharePoint), draw.io often returns
 * "The resource could not be found". draw.io does not reliably support programmatic
 * links to OneDrive/SharePoint files. In the UI we therefore open the SharePoint
 * file view URL; users choose "Open with draw.io" there.
 *
 * @param fileId – Graph API drive item ID of the file
 * @see https://www.drawio.com/doc/faq/supported-location-hash-properties (W = OneDrive)
 */
export function getDrawioOneDriveUrl(fileId: string): string {
  return `https://app.diagrams.net/?mode=onedrive#W${encodeURIComponent(fileId)}`;
}

/**
 * Builds a direct file URL from the Graph API webUrl (often DispForm.aspx)
 * that opens the SharePoint file view – there you get "Open" / "Open with draw.io".
 *
 * @param file – name, webUrl, path (display path e.g. "Dokumente > General > trash")
 */
export function getSharePointFileViewUrl(file: {
  webUrl: string;
  name: string;
  path?: string;
}): string {
  const formsIndex = file.webUrl.indexOf('/Forms/');
  if (formsIndex === -1) {
    return file.webUrl;
  }
  const baseAndLibrary = file.webUrl.slice(0, formsIndex);
  const pathSegments = file.path
    ? file.path.replace(/\s*>\s*/g, '/').split('/').filter(Boolean).map(encodeURIComponent).join('/')
    : '';
  const fileName = encodeURIComponent(file.name);
  return pathSegments
    ? `${baseAndLibrary}/${pathSegments}/${fileName}`
    : `${baseAndLibrary}/${fileName}`;
}

/**
 * Opens app.diagrams.net in OneDrive mode (including SharePoint in the "Open" dialog).
 * Users can then choose "Open" → SharePoint → navigate to the file – then saving
 * and collaboration in OneDrive/SharePoint work. There is no URL parameter that
 * pre-selects a specific SharePoint file.
 */
export function getDrawioOneDriveModeUrl(): string {
  return 'https://app.diagrams.net/?mode=onedrive&splash=0';
}

/**
 * Opens a SharePoint/OneDrive file directly in app.diagrams.net with collaboration.
 *
 * draw.io internally uses the format `#W{driveId}/{itemId}` to load a file via
 * Graph API `/drives/{driveId}/items/{itemId}`. This avoids manual navigation
 * via "Open → SharePoint → …". Saving and real-time collaboration work directly.
 *
 * @param driveId – Graph API drive ID (from parentReference.driveId)
 * @param itemId  – Graph API drive item ID of the file
 * @see https://www.drawio.com/doc/faq/supported-location-hash-properties (W = OneDrive/SharePoint)
 */
export function getDrawioSharePointDirectUrl(driveId: string, itemId: string): string {
  return `https://app.diagrams.net/#W${encodeURIComponent(driveId)}/${encodeURIComponent(itemId)}`;
}

/**
 * Opens a diagram in app.diagrams.net that is loaded from a URL.
 * The URL must serve the raw content (XML) of the .drawio file (e.g. Graph API downloadUrl).
 *
 * @param contentUrl – URL that serves the diagram XML (GET)
 * @see https://www.drawio.com/doc/faq/supported-location-hash-properties (U = URL)
 */
export function getDrawioOpenFromUrl(contentUrl: string): string {
  return `https://app.diagrams.net/#U${encodeURIComponent(contentUrl)}`;
}
